/**
 * Start-wave times: one reader for every surface.
 *
 * `start_waves.start_time` / `end_time` are timestamptz columns, but the rows in
 * them come in two shapes, and reading one shape as the other moves the time by
 * the event's UTC offset (an hour in Irish or UK summer):
 *
 *   1. WALL-CLOCK AS UTC. The organiser's clock time stored as if it were UTC:
 *      "09:00" is saved as 09:00Z, so the UTC hours ARE the local time. Two
 *      writers produced this shape:
 *        - parseWallClockTime (lib/utils/timezone.ts, since 29 May 2026)
 *          anchors the clock on 1970-01-01.
 *        - the parser it replaced anchored the clock on the DAY THE WAVE WAS
 *          SAVED (`new Date(); setUTCHours(h, m)`), so those rows carry an
 *          arbitrary 2026 date from before 30 May 2026. Duplicating an event
 *          or a wave copies the value, date and all, so it keeps that shape.
 *   2. TRUE INSTANT. A real moment on the event date: heats written by the
 *      wave scheduler (lib/schedule) and waves seeded or posted with a full
 *      ISO datetime. These are read in the EVENT's timezone.
 *
 * Telling them apart: no writer has produced the wall-clock shape with a date
 * on or after 30 May 2026, and nothing writes a true instant before it, so
 * anything earlier than that cut-over is wall-clock-as-UTC and everything
 * from it onwards is a true instant. This needs no event date, which matters
 * because an organiser can move the event date after the waves exist.
 *
 * Never read a wave time with getHours/toLocaleTimeString in the host zone, and
 * never push a wall-clock row through the event zone: 1970 in Europe/London was
 * British Standard Time (UTC+1 all year, 1968 to 1971), so even a "winter"
 * 1970 anchor would shift by an hour.
 *
 * Lives in @fitvent/types so the database queries can order waves by the same
 * clock the app displays. Apps import it through apps/web/lib/utils/wave-time.
 */
/** 2026-05-30T00:00Z. Wave times before this are wall-clock-as-UTC. */
export const WALL_CLOCK_CUTOVER_MS = Date.UTC(2026, 4, 30);

export type WaveTimeValue = Date | string | number | null | undefined;

function toDate(value: WaveTimeValue): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** True when the stored value is an organiser's clock time encoded as UTC. */
export function isWallClockWaveTime(value: WaveTimeValue): boolean {
  const d = toDate(value);
  return d !== null && d.getTime() < WALL_CLOCK_CUTOVER_MS;
}

function zonedClock(d: Date, timeZone: string): { hour: number; minute: number } {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    minute: 'numeric',
    hourCycle: 'h23',
  });
  let hour = 0;
  let minute = 0;
  for (const p of fmt.formatToParts(d)) {
    if (p.type === 'hour') hour = Number(p.value) % 24;
    if (p.type === 'minute') minute = Number(p.value);
  }
  return { hour, minute };
}

/**
 * The wave's clock time as hours and minutes on the event's wall clock.
 * A missing or unreadable timezone falls back to UTC, never the host zone.
 */
export function waveClockParts(
  value: WaveTimeValue,
  timeZone?: string | null,
): { hour: number; minute: number } | null {
  const d = toDate(value);
  if (!d) return null;
  if (d.getTime() < WALL_CLOCK_CUTOVER_MS) {
    return { hour: d.getUTCHours(), minute: d.getUTCMinutes() };
  }
  try {
    return zonedClock(d, timeZone || 'UTC');
  } catch {
    return zonedClock(d, 'UTC');
  }
}

/** "09:00" on the event's wall clock, or null for a missing or unreadable value. */
export function waveClock(value: WaveTimeValue, timeZone?: string | null): string | null {
  const p = waveClockParts(value, timeZone);
  return p ? `${pad(p.hour)}:${pad(p.minute)}` : null;
}

/** "9:00 am" on the event's wall clock, for surfaces that use the 12-hour clock. */
export function waveClock12(value: WaveTimeValue, timeZone?: string | null): string | null {
  const p = waveClockParts(value, timeZone);
  if (!p) return null;
  const h = p.hour % 12 === 0 ? 12 : p.hour % 12;
  return `${h}:${pad(p.minute)} ${p.hour < 12 ? 'am' : 'pm'}`;
}

/**
 * "09:00 to 09:30", "09:00" when there is no end, "Until 09:30" when there is
 * only an end, null when there is neither. `separator` lets a compact surface
 * use an en dash.
 */
export function waveRange(
  start: WaveTimeValue,
  end: WaveTimeValue,
  timeZone?: string | null,
  separator = ' to ',
): string | null {
  const s = waveClock(start, timeZone);
  const e = waveClock(end, timeZone);
  if (s && e) return `${s}${separator}${e}`;
  if (s) return s;
  if (e) return `Until ${e}`;
  return null;
}

/**
 * Minutes after midnight on the event's wall clock: a sort key that orders
 * waves of both shapes on one event. Null for a missing or unreadable value.
 */
export function waveMinutes(value: WaveTimeValue, timeZone?: string | null): number | null {
  const p = waveClockParts(value, timeZone);
  return p ? p.hour * 60 + p.minute : null;
}

/** Comparator for Array.sort: earliest wave first, waves without a time last. */
export function compareWaveTimes(a: WaveTimeValue, b: WaveTimeValue, timeZone?: string | null): number {
  const ma = waveMinutes(a, timeZone);
  const mb = waveMinutes(b, timeZone);
  if (ma === null && mb === null) return 0;
  if (ma === null) return 1;
  if (mb === null) return -1;
  return ma - mb;
}
